import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TitleBar from "../../common/title-bar/title-bar";
import Tags from "../../common/tags";
import Text from "../../common/typography/text";
import { ConfigProvider, Steps, Spin, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { colors } from "../../theme/colors";
import Button from "../../common/button/button";
import RightSmallArrowIcon from "../../icons/right-small-arrow-icon";
import LeftSmallArrowIcon from "../../icons/left-small-arrow-icon";
import ProductsContent from "./Products/ProductsContent";
import ProductsReview from "./Products/ProductsReview";
import ProcedureContent from "./Procedures/ProcedureContent";
import ProcedureReview from "./Procedures/ProcedureReview";
import ShippingContent from "./Shipping/ShippingContent";
import ShippingReview from "./Shipping/ShippingReview";
import ConfirmationReview from "./Confirmation/ConfirmationReview";
import ConfirmationContent from "./Confirmation/ConfirmationContent";
import { useLocation, useNavigate } from "react-router-dom";
import { SHIPPING_COSTS } from "../IVR/QuickOrder";
import { useScreen } from "../Utils/Screens";
import {
  canAccessNewDirectOrder,
  getDefaultHomePath,
} from "../Utils/accessControl";
import APIService from "../../Service/APIService";
import { useAuth } from "../AuthProvider";
import dayjs from "dayjs";

const STRIPE_CARD_SURCHARGE_PERCENT = 0.029;
const STRIPE_FIXED_FEE = 0.3;
const STRIPE_BANK_SURCHARGE_PERCENT = 0.008;
const STRIPE_BANK_SURCHARGE_CAP = 5;

const toComparableId = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? String(value) : numericValue;
};

const matchesSelectedProductScope = (
  option,
  manufacturerId,
  productGroupId,
) => {
  if (
    manufacturerId === null ||
    manufacturerId === undefined ||
    productGroupId === null ||
    productGroupId === undefined
  ) {
    return true;
  }

  return (
    toComparableId(option?.ManufacturerID) === manufacturerId &&
    toComparableId(option?.ProductGroupID) === productGroupId
  );
};

const toPositiveNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const getFirstPositiveNumber = (...values) => {
  for (const value of values) {
    const normalizedValue = toPositiveNumber(value);
    if (normalizedValue !== null) {
      return normalizedValue;
    }
  }
  return null;
};

const getFirstNonEmptyString = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    const normalizedValue = String(value).trim();
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
};

const toISOStringOrNull = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toISOString === "function") {
    return value.toISOString();
  }

  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue.toISOString() : null;
};

const buildUserFullName = (user) =>
  getFirstNonEmptyString(
    [user?.FirstName, user?.LastName].filter(Boolean).join(" "),
    user?.Name,
  );

const normalizeBoolean = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const getStockQty = (product) => {
  const parsed = Number(product?.StockQty ?? product?.stockQty ?? 0);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

const isStockManaged = (product) =>
  normalizeBoolean(product?.ManageStock ?? product?.manageStock);

const formatStockShortageMessage = (shortages = []) => {
  if (!Array.isArray(shortages) || shortages.length === 0) {
    return "One or more selected products do not have enough available stock. Refresh products and try again.";
  }
  return shortages
    .map((item) => {
      const label = item.ProductCode || item.ProductName || `Product ${item.ProductID}`;
      return `${label}: requested ${item.RequestedQty}, available ${item.AvailableQty}`;
    })
    .join(" ");
};

const isExplicitFalse = (value) =>
  value === false || value === "false" || value === 0 || value === "0";

const normalizeShippingRateValue = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0
    ? Number(parsedValue.toFixed(2))
    : null;
};

const roundCurrency = (value) => Number(Number(value ?? 0).toFixed(2));

const calculateProcessingFee = ({
  paymentTabKey,
  priceTotal,
  shippingPreferenceCost,
  requiresPayment,
}) => {
  if (!requiresPayment) {
    return {
      cardProcessingFee: 0,
      bankProcessingFee: 0,
      processingFee: 0,
      processingFeeLabel: null,
    };
  }

  const subtotal = Number(priceTotal ?? 0) + Number(shippingPreferenceCost ?? 0);
  if (!Number.isFinite(subtotal) || subtotal <= 0) {
    return {
      cardProcessingFee: 0,
      bankProcessingFee: 0,
      processingFee: 0,
      processingFeeLabel: null,
    };
  }

  if (paymentTabKey === "bank-transfer") {
    const bankProcessingFee = roundCurrency(
      Math.min(STRIPE_BANK_SURCHARGE_CAP, subtotal * STRIPE_BANK_SURCHARGE_PERCENT)
    );
    return {
      cardProcessingFee: 0,
      bankProcessingFee,
      processingFee: bankProcessingFee,
      processingFeeLabel: bankProcessingFee > 0 ? "Bank Processing Fee" : null,
    };
  }

  const cardProcessingFee = roundCurrency(
    (subtotal + STRIPE_FIXED_FEE) / (1 - STRIPE_CARD_SURCHARGE_PERCENT) - subtotal
  );
  return {
    cardProcessingFee,
    bankProcessingFee: 0,
    processingFee: cardProcessingFee,
    processingFeeLabel: cardProcessingFee > 0 ? "Card Processing Fee" : null,
  };
};

const formatShippingRateValue = (value) =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

const buildShippingPreferenceOption = ({
  label,
  cost,
  value,
  ...rest
}) => {
  const normalizedLabel = getFirstNonEmptyString(label);
  const normalizedCost = normalizeShippingRateValue(cost);

  if (!normalizedLabel || normalizedCost === null) {
    return null;
  }

  return {
    ...rest,
    label: normalizedLabel,
    value: getFirstNonEmptyString(
      value,
      `${normalizedLabel} - $${formatShippingRateValue(normalizedCost)}`,
    ),
    cost: normalizedCost,
  };
};

const DEFAULT_SHIPPING_PREFERENCE_OPTIONS = [
  buildShippingPreferenceOption({
    label: "First Overnight by 8:00 AM",
    cost: 200,
  }),
  buildShippingPreferenceOption({
    label: "Priority Overnight by 10:30 AM",
    cost: 150,
  }),
  buildShippingPreferenceOption({
    label: "Standard Overnight by 5:00 PM",
    cost: 100,
  }),
  buildShippingPreferenceOption({
    label: "2-Day AM by 10:30 AM",
    cost: 50,
  }),
  buildShippingPreferenceOption({
    label: "2-Day by 5:00 PM",
    cost: 10,
  }),
].filter(Boolean);

const getShippingRatesFromCategory = (shippingCategory) => {
  const sourceRates =
    shippingCategory?.ShippingRates ||
    shippingCategory?.shippingRates ||
    shippingCategory?.Rates ||
    [];

  return Array.isArray(sourceRates) ? sourceRates : [];
};

const buildShippingPreferenceOptionsFromCategory = (shippingCategory) => {
  const seenOptions = new Set();

  return getShippingRatesFromCategory(shippingCategory).reduce(
    (options, rate) => {
      const nextOption = buildShippingPreferenceOption({
        label: rate?.ShippingRateName,
        cost: rate?.ShippingRate,
        ShippingRateID: getFirstPositiveNumber(rate?.ID, rate?.ShippingRateID),
        ShippingCategoryID: getFirstPositiveNumber(
          rate?.ShippingCategoryID,
          shippingCategory?.ID,
          shippingCategory?.ShippingCategoryID,
        ),
      });

      if (!nextOption) {
        return options;
      }

      const dedupeKey = `${nextOption.label}|${nextOption.cost}`;
      if (seenOptions.has(dedupeKey)) {
        return options;
      }

      seenOptions.add(dedupeKey);
      options.push(nextOption);
      return options;
    },
    [],
  );
};

const isSameShippingPreferenceOption = (leftOption, rightOption) =>
  getFirstNonEmptyString(leftOption?.value) ===
    getFirstNonEmptyString(rightOption?.value) &&
  getFirstNonEmptyString(leftOption?.label) ===
    getFirstNonEmptyString(rightOption?.label) &&
  normalizeShippingRateValue(leftOption?.cost) ===
    normalizeShippingRateValue(rightOption?.cost);

const getShippingPreferenceOptionKey = (option) =>
  getFirstNonEmptyString(option?.value, option?.label, option);

const FEDEX_SERVICE_OPTIONS = [
  { label: "FedEx First Overnight", value: "FIRST_OVERNIGHT" },
  { label: "FedEx Priority Overnight", value: "PRIORITY_OVERNIGHT" },
  { label: "FedEx Standard Overnight", value: "STANDARD_OVERNIGHT" },
  { label: "FedEx 2Day AM", value: "FEDEX_2_DAY_AM" },
  { label: "FedEx 2Day", value: "FEDEX_2_DAY" },
].map((option) => ({
  ...option,
  serviceType: option.value,
  source: "fedex",
}));
const FEDEX_SERVICE_TYPES = new Set(
  FEDEX_SERVICE_OPTIONS.map((option) => option.serviceType),
);

const getFedexServiceType = (option) => {
  const serviceType = getFirstNonEmptyString(option?.serviceType, option?.value);
  return FEDEX_SERVICE_TYPES.has(serviceType) ? serviceType : "";
};

const resolveProductGroupShippingMode = (productGroup) => {
  if (normalizeBoolean(productGroup?.UseFedexShipping)) return "fedex";
  if (normalizeBoolean(productGroup?.CustomShipping)) return "custom";
  return "standard";
};

const buildFedexShippingPreferenceOption = (quote, selectedOption) => {
  const cost = normalizeShippingRateValue(quote?.shippingCost);
  if (cost === null) {
    return null;
  }

  return {
    ...(selectedOption || {}),
    label:
      selectedOption?.label ||
      quote?.serviceName ||
      getFirstNonEmptyString(quote?.serviceType),
    value:
      selectedOption?.value ||
      quote?.serviceType ||
      getFirstNonEmptyString(quote?.serviceName),
    cost,
    source: "fedex",
    serviceType:
      selectedOption?.serviceType ||
      quote?.serviceType ||
      getFirstNonEmptyString(selectedOption?.value),
    serviceName: quote?.serviceName || selectedOption?.label,
    currency: quote?.currency || "USD",
  };
};

const buildFedexQuoteRequestKey = ({
  effectiveShipToAccountID,
  resolvedShipToAccountDetails,
  orderProducts,
  fedexServiceType,
}) => {
  const normalizedProducts = orderProducts
    .map((item) => ({
      ProductID: getFirstPositiveNumber(item?.ProductID, item?.ID),
      ProductGroupID: getFirstPositiveNumber(item?.ProductGroupID),
      Quantity: Number(item?.Quantity ?? 0),
    }))
    .filter((item) => item.ProductID && item.Quantity > 0)
    .sort((left, right) => {
      const byProduct = Number(left.ProductID) - Number(right.ProductID);
      if (byProduct !== 0) return byProduct;
      return Number(left.ProductGroupID ?? 0) - Number(right.ProductGroupID ?? 0);
    });

  return JSON.stringify({
    ShipToAccountID: effectiveShipToAccountID ?? null,
    FedexServiceType: fedexServiceType || null,
    ShipToAddress: {
      AddressLine1: getFirstNonEmptyString(
        resolvedShipToAccountDetails?.AddressLine1,
      ),
      AddressLine2: getFirstNonEmptyString(
        resolvedShipToAccountDetails?.AddressLine2,
      ),
      City: getFirstNonEmptyString(resolvedShipToAccountDetails?.City),
      State: getFirstNonEmptyString(resolvedShipToAccountDetails?.State),
      Zip: getFirstNonEmptyString(resolvedShipToAccountDetails?.Zip),
      CountryCode: "US",
    },
    OrderProducts: normalizedProducts,
  });
};

const buildFedexQuotePayload = ({
  effectiveShipToAccountID,
  resolvedShipToAccountDetails,
  orderProducts,
  fedexServiceType,
}) => ({
  ShipToAccountID: effectiveShipToAccountID,
  FedexServiceType: fedexServiceType,
  ShipToAddress: {
    AddressLine1: getFirstNonEmptyString(
      resolvedShipToAccountDetails?.AddressLine1,
    ),
    AddressLine2: getFirstNonEmptyString(
      resolvedShipToAccountDetails?.AddressLine2,
    ),
    City: getFirstNonEmptyString(resolvedShipToAccountDetails?.City),
    State: getFirstNonEmptyString(resolvedShipToAccountDetails?.State),
    Zip: getFirstNonEmptyString(resolvedShipToAccountDetails?.Zip),
    CountryCode: "US",
  },
  OrderProducts: (orderProducts || []).map((item) => ({
    ProductID: getFirstPositiveNumber(item?.ProductID, item?.ID),
    ProductGroupID: getFirstPositiveNumber(item?.ProductGroupID),
    Quantity: Number(item?.Quantity ?? 0),
  })),
});

const getFedexQuoteErrorMessage = (error) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  (Array.isArray(error?.response?.data?.errors)
    ? error.response.data.errors.join(" ")
    : "") ||
  error?.message ||
  "Unable to fetch FedEx shipping rate. Please try again.";

const DIRECT_ORDER_CHECKOUT_LAUNCH_KEY_PREFIX = "direct-order-checkout-launch";

const createSubmissionId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `direct-order-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getCheckoutLaunchKey = (orderId) =>
  `${DIRECT_ORDER_CHECKOUT_LAUNCH_KEY_PREFIX}:${orderId}`;

const setCheckoutLaunchIntent = (orderId, checkoutUrl) => {
  if (!orderId || !checkoutUrl) {
    return false;
  }

  try {
    window.sessionStorage.setItem(
      getCheckoutLaunchKey(orderId),
      JSON.stringify({
        checkoutUrl,
        createdAt: Date.now(),
      }),
    );
    return true;
  } catch (error) {
    console.error("[NewDirectOrder] Failed to persist checkout launch intent", error);
    return false;
  }
};

const getOrderIdFromViewOrderPath = (viewOrderPath) => {
  const matchedOrderId = String(viewOrderPath ?? "").match(/\/ViewNewDirectOrder\/(\d+)/i);
  if (!matchedOrderId?.[1]) {
    return null;
  }

  const parsedOrderId = Number(matchedOrderId[1]);
  return Number.isFinite(parsedOrderId) && parsedOrderId > 0 ? parsedOrderId : null;
};

const NewDirectOrder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAccessPage = canAccessNewDirectOrder(user);
  const {
    AccountID,
    AccountName,
    ShipToAccountID,
    ShipTo,
    ShipToAccountDetails: initialShipToAccountDetails,
    CaseID,
    SelectedCase,
  } = location?.state || {};
  const normalizedAccountName = AccountName || "";
  const normalizedShipToName = ShipTo || normalizedAccountName;
  const normalizedShipToAccountDetails =
    initialShipToAccountDetails && typeof initialShipToAccountDetails === "object"
      ? initialShipToAccountDetails
      : null;
  const effectiveShipToAccountID = useMemo(() => {
    const parsedShipToAccountID = Number(ShipToAccountID || AccountID);
    return Number.isFinite(parsedShipToAccountID) && parsedShipToAccountID > 0
      ? parsedShipToAccountID
      : null;
  }, [AccountID, ShipToAccountID]);
  const normalizedAccountID = useMemo(() => {
    const parsedAccountID = Number(AccountID);
    return Number.isFinite(parsedAccountID) && parsedAccountID > 0
      ? parsedAccountID
      : null;
  }, [AccountID]);
  const caseScopedManufacturerID = useMemo(
    () => toComparableId(SelectedCase?.ManufacturerID),
    [SelectedCase?.ManufacturerID],
  );
  const caseScopedProductGroupID = useMemo(
    () => toComparableId(SelectedCase?.ProductGroupID),
    [SelectedCase?.ProductGroupID],
  );
  const caseScopedManufacturerName = getFirstNonEmptyString(
    SelectedCase?.ManufacturerName,
  );
  const caseScopedProductGroupName = getFirstNonEmptyString(
    SelectedCase?.ProductGroupName,
  );

  useEffect(() => {
    if (!user || canAccessPage) {
      return;
    }

    const redirectPath = getDefaultHomePath(user);
    if (window.location.pathname !== redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [canAccessPage, navigate, user]);

  // Products data
  const [currentStep, setCurrentStep] = useState(0);
  const [productCodes, setProductCodes] = useState([]);
  const [productCodeData, setProductCodeData] = useState([]);
  const [allProductOptions, setAllProductOptions] = useState([]);
  const [isLoadingProductOptions, setIsLoadingProductOptions] = useState(false);
  const [selectedManufacturerID, setSelectedManufacturerID] = useState(null);
  const [selectedProductGroupID, setSelectedProductGroupID] = useState(null);
  const productFetchRequestIdRef = useRef(0);

  // Procedure data
  const [providerName, setProviderName] = useState();
  const [applicationDate, setApplicationDate] = useState();
  const [firstNameInitials, setFirstNameInitials] = useState("");
  const [lastNameInitials, setLastNameInitials] = useState("");
  const [applicationNumber, setApplicationNumber] = useState();
  const [providerOptions, setProviderOptions] = useState([]);
  const [isLoadingProviderOptions, setIsLoadingProviderOptions] = useState(false);
  const providerFetchRequestIdRef = useRef(0);

  // Shipping data
  const [shippingPreference, setShippingPreference] = useState(false);
  const [shippingPreferenceCost, setShippingPreferenceCost] = useState(false);
  const [shippingCategories, setShippingCategories] = useState([]);
  const [isLoadingShippingCategories, setIsLoadingShippingCategories] =
    useState(false);
  const [shippingContact, setShippingContact] = useState("");
  const shippingCategoriesFetchRequestIdRef = useRef(0);
  const [fedexQuote, setFedexQuote] = useState(null);
  const [fedexQuoteStatus, setFedexQuoteStatus] = useState("idle");
  const [fedexQuoteError, setFedexQuoteError] = useState("");
  const [lastSuccessfulFedexQuoteKey, setLastSuccessfulFedexQuoteKey] =
    useState("");
  const [pendingFedexQuoteAfterAccountDetails, setPendingFedexQuoteAfterAccountDetails] =
    useState(false);
  const fedexQuoteRequestIdRef = useRef(0);
  const fedexQuoteInFlightKeyRef = useRef("");
  const fedexQuoteLatestKeyRef = useRef("");
  const previousFedexQuoteRequestKeyRef = useRef("");
  const wasFedexShippingEnabledRef = useRef(false);
  const previousShippingModeRef = useRef(null);
  const [customPONumber, setCustomPONumber] = useState("");
  const [accountDetails, setAccountDetails] = useState(null);
  const [resolvedShipToAccountDetails, setResolvedShipToAccountDetails] =
    useState(normalizedShipToAccountDetails);
  const [billToAccountDetails, setBillToAccountDetails] = useState(null);
  const [isLoadingAccountDetails, setIsLoadingAccountDetails] = useState(false);
  const accountDetailsFetchRequestIdRef = useRef(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionIdRef = useRef(createSubmissionId());

  // Confirmation
  const [activeTab, setActiveTab] = useState({
    key: "card-payment",
    text: "Pay by Card",
  });
  const hasSelectedProducts = (productCodeData || []).some(
    (item) => Number(item?.Quantity) > 0,
  );
  const procedureTagText =
    getFirstNonEmptyString(providerName?.AccountContactType) || "";

  const isPhone = !useScreen("md");
  const placedByName = buildUserFullName(user);

  useEffect(() => {
    setResolvedShipToAccountDetails(normalizedShipToAccountDetails);
  }, [normalizedShipToAccountDetails]);

  useEffect(() => {
    const parsedAccountID = Number(AccountID);

    setProductCodes([]);
    setProductCodeData([]);
    setSelectedManufacturerID(caseScopedManufacturerID);
    setSelectedProductGroupID(caseScopedProductGroupID);
    setAllProductOptions([]);
    setIsLoadingProductOptions(false);

    if (!Number.isFinite(parsedAccountID) || parsedAccountID <= 0) {
      return;
    }

    const currentRequestId = productFetchRequestIdRef.current + 1;
    productFetchRequestIdRef.current = currentRequestId;
    let isCancelled = false;

    const fetchProductOptions = async () => {
      setIsLoadingProductOptions(true);
      try {
        const apiService = new APIService();
        const response = await apiService.getDataFromBackend(
          `newdirectorder/productoptions?accountID=${encodeURIComponent(
            parsedAccountID,
          )}`,
        );

        const fetchedItems = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];

        const normalizedOptions = fetchedItems
          .map((item) => ({
            value: item?.ProductID,
            label:
              item?.label ||
              `${item?.ProductCode ?? ""}${item?.ProductName ? ` - ${item.ProductName}` : ""}`,
            ProductID: item?.ProductID,
            ProductCode: item?.ProductCode,
            ProductName: item?.ProductName,
            ManufacturerID: item?.ManufacturerID,
            ManufacturerName: item?.ManufacturerName,
            ProductGroupID: item?.ProductGroupID,
            ProductGroupName: item?.ProductGroupName,
            CollectPayment: item?.CollectPayment !== false,
            CustomShipping: normalizeBoolean(item?.CustomShipping),
            UseFedexShipping: normalizeBoolean(item?.UseFedexShipping),
            ShippingCategoryID: getFirstPositiveNumber(
              item?.ShippingCategoryID,
            ),
            RequireProcedureInfo: item?.RequireProcedureInfo,
            ManageStock: isStockManaged(item),
            StockQty: getStockQty(item),
            Price: Number(item?.Price ?? 0),
          }))
          .filter(
            (item) =>
              item?.ProductID &&
              item?.ProductCode &&
              item?.ProductName &&
              item?.ManufacturerID,
          );

        if (!isCancelled && productFetchRequestIdRef.current === currentRequestId) {
          setAllProductOptions(normalizedOptions);
        }
      } catch (error) {
        console.error("[NewDirectOrder] Failed to fetch product options", error);
        if (!isCancelled && productFetchRequestIdRef.current === currentRequestId) {
          setAllProductOptions([]);
        }
      } finally {
        if (!isCancelled && productFetchRequestIdRef.current === currentRequestId) {
          setIsLoadingProductOptions(false);
        }
      }
    };

    fetchProductOptions();

    return () => {
      isCancelled = true;
    };
  }, [AccountID, caseScopedManufacturerID, caseScopedProductGroupID]);

  useEffect(() => {
    setSelectedManufacturerID(caseScopedManufacturerID);
    setSelectedProductGroupID(caseScopedProductGroupID);
  }, [caseScopedManufacturerID, caseScopedProductGroupID]);

  useEffect(() => {
    setShippingCategories([]);
    setIsLoadingShippingCategories(false);

    const currentRequestId = shippingCategoriesFetchRequestIdRef.current + 1;
    shippingCategoriesFetchRequestIdRef.current = currentRequestId;
    let isCancelled = false;

    const fetchShippingCategories = async () => {
      setIsLoadingShippingCategories(true);
      try {
        const apiService = new APIService();
        const response = await apiService.getDataFromBackend(
          "getshippingcategoryoptions",
        );

        const fetchedItems = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        const normalizedCategories = fetchedItems
          .map((item) => ({
            ID: getFirstPositiveNumber(item?.ID, item?.ShippingCategoryID),
            ShippingCategoryID: getFirstPositiveNumber(
              item?.ShippingCategoryID,
              item?.ID,
            ),
            ShippingCategoryName: getFirstNonEmptyString(
              item?.ShippingCategoryName,
            ),
            ShippingRates: getShippingRatesFromCategory(item)
              .map((rate) => ({
                ID: getFirstPositiveNumber(rate?.ID, rate?.ShippingRateID),
                ShippingRateID: getFirstPositiveNumber(
                  rate?.ShippingRateID,
                  rate?.ID,
                ),
                ShippingCategoryID: getFirstPositiveNumber(
                  rate?.ShippingCategoryID,
                  item?.ID,
                  item?.ShippingCategoryID,
                ),
                ShippingRateName: getFirstNonEmptyString(
                  rate?.ShippingRateName,
                ),
                ShippingRate: normalizeShippingRateValue(rate?.ShippingRate),
              }))
              .filter(
                (rate) =>
                  rate?.ShippingRateName && rate?.ShippingRate !== null,
              ),
          }))
          .filter((item) => item?.ID);

        if (
          !isCancelled &&
          shippingCategoriesFetchRequestIdRef.current === currentRequestId
        ) {
          setShippingCategories(normalizedCategories);
        }
      } catch (error) {
        console.error(
          "[NewDirectOrder] Failed to fetch shipping category options",
          error,
        );
        if (
          !isCancelled &&
          shippingCategoriesFetchRequestIdRef.current === currentRequestId
        ) {
          setShippingCategories([]);
        }
      } finally {
        if (
          !isCancelled &&
          shippingCategoriesFetchRequestIdRef.current === currentRequestId
        ) {
          setIsLoadingShippingCategories(false);
        }
      }
    };

    fetchShippingCategories();

    return () => {
      isCancelled = true;
    };
  }, []);

  const fetchProviderOptions = useCallback(async (accountID, providerToSelect = null) => {
    const currentRequestId = providerFetchRequestIdRef.current + 1;
    providerFetchRequestIdRef.current = currentRequestId;

    setIsLoadingProviderOptions(true);
    try {
      const apiService = new APIService();
      const response = await apiService.getDataFromBackend(
        `newdirectorder/provideroptions?accountID=${encodeURIComponent(
          accountID,
        )}`,
      );

      const fetchedItems = Array.isArray(response?.data?.items)
        ? response.data.items
        : [];

      const normalizedOptions = fetchedItems
        .map((item) => ({
          value: item?.value,
          label: item?.label,
          UserID: item?.UserID,
          AccountUserID: item?.AccountUserID,
          FirstName: item?.FirstName,
          LastName: item?.LastName,
          Email: item?.Email ?? null,
          Phone: item?.Phone ?? null,
          AccountContactType: item?.AccountContactType ?? null,
          ProviderNPI: item?.ProviderNPI ?? null,
        }))
        .filter((item) => item?.value && item?.label);

      if (providerFetchRequestIdRef.current === currentRequestId) {
        setProviderOptions(normalizedOptions);
        if (providerToSelect) {
          const selectedOption = normalizedOptions.find(
            (option) =>
              Number(option.AccountUserID) === Number(providerToSelect.AccountUserID),
          );
          setProviderName(selectedOption || providerToSelect);
        }
      }
    } catch (error) {
      console.error("[NewDirectOrder] Failed to fetch provider options", error);
      if (providerFetchRequestIdRef.current === currentRequestId) {
        setProviderOptions([]);
      }
    } finally {
      if (providerFetchRequestIdRef.current === currentRequestId) {
        setIsLoadingProviderOptions(false);
      }
    }
  }, []);

  const handleProviderCreated = useCallback(async (createdProvider) => {
    if (!normalizedAccountID) return;
    await fetchProviderOptions(normalizedAccountID, createdProvider);
  }, [fetchProviderOptions, normalizedAccountID]);

  useEffect(() => {
    setProviderName(undefined);
    setProviderOptions([]);
    setIsLoadingProviderOptions(false);

    if (normalizedAccountID) {
      fetchProviderOptions(normalizedAccountID);
    }
  }, [fetchProviderOptions, normalizedAccountID]);

  useEffect(() => {
    setAccountDetails(null);
    setBillToAccountDetails(null);
    setIsLoadingAccountDetails(false);

    if (!normalizedAccountID && !effectiveShipToAccountID) {
      setResolvedShipToAccountDetails(normalizedShipToAccountDetails);
      return;
    }

    const currentRequestId = accountDetailsFetchRequestIdRef.current + 1;
    accountDetailsFetchRequestIdRef.current = currentRequestId;
    let isCancelled = false;

    const fetchAccountDetails = async () => {
      setIsLoadingAccountDetails(true);
      try {
        const apiService = new APIService();
        const [accountResponse, shipToResponse] = await Promise.all([
          normalizedAccountID
            ? apiService.getDataFromBackend(
                `getaccountbyid?ID=${encodeURIComponent(normalizedAccountID)}`,
              )
            : Promise.resolve(null),
          effectiveShipToAccountID
            ? apiService.getDataFromBackend(
                `getaccountbyid?ID=${encodeURIComponent(effectiveShipToAccountID)}`,
              )
            : Promise.resolve(null),
        ]);

        const nextAccountDetails = accountResponse?.data?.data ?? null;
        const nextShipToAccountDetails =
          shipToResponse?.data?.data ??
          nextAccountDetails ??
          normalizedShipToAccountDetails ??
          null;

        const billToAccountID = getFirstPositiveNumber(
          nextShipToAccountDetails?.BillingAccountID,
          nextAccountDetails?.BillingAccountID,
          nextAccountDetails?.ID,
        );

        const billToResponse = billToAccountID
          ? await apiService.getDataFromBackend(
              `getaccountbyid?ID=${encodeURIComponent(billToAccountID)}`,
            )
          : null;

        if (
          !isCancelled &&
          accountDetailsFetchRequestIdRef.current === currentRequestId
        ) {
          setAccountDetails(nextAccountDetails);
          setResolvedShipToAccountDetails(nextShipToAccountDetails);
          setBillToAccountDetails(
            billToResponse?.data?.data ?? nextAccountDetails ?? null,
          );
        }
      } catch (error) {
        console.error(
          "[NewDirectOrder] Failed to fetch account details for payload",
          error,
        );

        if (
          !isCancelled &&
          accountDetailsFetchRequestIdRef.current === currentRequestId
        ) {
          setResolvedShipToAccountDetails(normalizedShipToAccountDetails);
          setBillToAccountDetails(null);
        }
      } finally {
        if (
          !isCancelled &&
          accountDetailsFetchRequestIdRef.current === currentRequestId
        ) {
          setIsLoadingAccountDetails(false);
        }
      }
    };

    fetchAccountDetails();

    return () => {
      isCancelled = true;
    };
  }, [
    effectiveShipToAccountID,
    normalizedAccountID,
    normalizedShipToAccountDetails,
  ]);

  const availableProductOptions = useMemo(() => {
    if (selectedManufacturerID == null || selectedProductGroupID == null) {
      return allProductOptions;
    }

    return allProductOptions.filter(
      (option) =>
        matchesSelectedProductScope(
          option,
          selectedManufacturerID,
          selectedProductGroupID,
      ),
    );
  }, [allProductOptions, selectedManufacturerID, selectedProductGroupID]);

  const selectedProductGroupOption = useMemo(() => {
    const matchedProductOption = allProductOptions.find((option) =>
      matchesSelectedProductScope(
        option,
        selectedManufacturerID,
        selectedProductGroupID,
      ),
    );

    if (matchedProductOption) {
      return matchedProductOption;
    }

    return (
      (productCodeData || []).find((item) =>
        matchesSelectedProductScope(
          item,
          selectedManufacturerID,
          selectedProductGroupID,
        ),
      ) || null
    );
  }, [
    allProductOptions,
    productCodeData,
    selectedManufacturerID,
    selectedProductGroupID,
  ]);

  const selectedProductGroupShippingCategoryID = useMemo(
    () => getFirstPositiveNumber(selectedProductGroupOption?.ShippingCategoryID),
    [selectedProductGroupOption?.ShippingCategoryID],
  );

  const shouldShowProcedureStep = useMemo(() => {
    if (selectedProductGroupID == null || !selectedProductGroupOption) {
      return true;
    }

    return !isExplicitFalse(selectedProductGroupOption?.RequireProcedureInfo);
  }, [
    selectedProductGroupID,
    selectedProductGroupOption,
  ]);

  const shippingMode = useMemo(
    () => resolveProductGroupShippingMode(selectedProductGroupOption),
    [selectedProductGroupOption],
  );
  const isCustomShippingEnabled = shippingMode === "custom";
  const isFedexShippingEnabled = shippingMode === "fedex";

  useEffect(() => {
    if (
      previousShippingModeRef.current &&
      previousShippingModeRef.current !== shippingMode
    ) {
      setShippingPreference(false);
      setShippingPreferenceCost(0);
    }
    previousShippingModeRef.current = shippingMode;
  }, [shippingMode]);

  const shippingPreferenceOptions = useMemo(() => {
    if (isFedexShippingEnabled) {
      return FEDEX_SERVICE_OPTIONS;
    }

    if (!isCustomShippingEnabled) {
      return DEFAULT_SHIPPING_PREFERENCE_OPTIONS;
    }

    if (!selectedProductGroupShippingCategoryID) {
      return [];
    }

    const matchedShippingCategory = shippingCategories.find(
      (category) =>
        getFirstPositiveNumber(category?.ID, category?.ShippingCategoryID) ===
        selectedProductGroupShippingCategoryID,
    );

    if (!matchedShippingCategory) {
      return [];
    }

    return buildShippingPreferenceOptionsFromCategory(matchedShippingCategory);
  }, [
    isFedexShippingEnabled,
    isCustomShippingEnabled,
    selectedProductGroupShippingCategoryID,
    shippingCategories,
  ]);

  const shippingPreferencePlaceholder = useMemo(() => {
    if (isFedexShippingEnabled) {
      return "Select FedEx Shipping Preference";
    }

    if (!isCustomShippingEnabled) {
      return "Select Shipping Preference";
    }

    if (isLoadingShippingCategories) {
      return "Loading shipping preferences...";
    }

    if (!selectedProductGroupShippingCategoryID) {
      return "Shipping rates are not configured for this product group";
    }

    if (shippingPreferenceOptions.length === 0) {
      return "No shipping rates available for this product group";
    }

    return "Select Shipping Preference";
  }, [
    isFedexShippingEnabled,
    isCustomShippingEnabled,
    isLoadingShippingCategories,
    selectedProductGroupShippingCategoryID,
    shippingPreferenceOptions.length,
  ]);

  const isShippingPreferenceDisabled = useMemo(
    () =>
      !isFedexShippingEnabled &&
      isCustomShippingEnabled &&
      (isLoadingShippingCategories ||
        !selectedProductGroupShippingCategoryID ||
        shippingPreferenceOptions.length === 0),
    [
      isFedexShippingEnabled,
      isCustomShippingEnabled,
      isLoadingShippingCategories,
      selectedProductGroupShippingCategoryID,
      shippingPreferenceOptions.length,
    ],
  );

  const selectedManufacturerName = useMemo(() => {
    if (!selectedManufacturerID) return "";
    const selectedOption = allProductOptions.find(
      (option) =>
        matchesSelectedProductScope(
          option,
          selectedManufacturerID,
          selectedProductGroupID,
        ),
    );
    return selectedOption?.ManufacturerName || caseScopedManufacturerName;
  }, [
    allProductOptions,
    caseScopedManufacturerName,
    selectedManufacturerID,
    selectedProductGroupID,
  ]);

  const selectedProductGroupName = useMemo(() => {
    if (selectedProductGroupID == null) return "";
    const selectedOption = allProductOptions.find(
      (option) =>
        matchesSelectedProductScope(
          option,
          selectedManufacturerID,
          selectedProductGroupID,
        ),
    );
    return selectedOption?.ProductGroupName || caseScopedProductGroupName;
  }, [
    allProductOptions,
    caseScopedProductGroupName,
    selectedManufacturerID,
    selectedProductGroupID,
  ]);

  const reviewManufacturerName = useMemo(() => {
    if (productCodeData?.length > 0) {
      return productCodeData[0]?.ManufacturerName || selectedManufacturerName;
    }
    return "";
  }, [productCodeData, selectedManufacturerName]);

  const reviewProductGroupName = useMemo(() => {
    const groupNames = Array.from(
      new Set(
        (productCodeData || [])
          .map((item) => item?.ProductGroupName)
          .filter((name) => Boolean(name)),
      ),
    );
    return groupNames.join(", ");
  }, [productCodeData]);

  const RENDER_PAYMENT_OPTIONS = [
    {
      key: "card-payment",
      text: "Pay By Card",
      onClick: () => setActiveTab({ key: "card-payment", text: "Pay By Card" }),
    },
    {
      key: "bank-transfer",
      text: "Pay by Bank Transfer",
      onClick: () =>
        setActiveTab({ key: "bank-transfer", text: "Pay by Bank Transfer" }),
    },
  ];

  const getShippingCost = useCallback((option) => {
    const explicitCost = normalizeShippingRateValue(
      option?.cost ?? option?.ShippingRate,
    );
    if (explicitCost !== null) {
      return explicitCost;
    }

    const key =
      option?.value ??
      option?.label ??
      (typeof option === "string" ? option : "");
    if (!key) {
      return 0;
    }
    return SHIPPING_COSTS[key] ?? 0;
  }, []);

  const handleShippingPreferenceChange = (option) => {
    if (isFedexShippingEnabled) {
      setShippingPreference(option);
      setShippingPreferenceCost(0);
      startFedexQuote({ force: true, shippingPreferenceOverride: option });
      return;
    }

    setShippingPreferenceCost(getShippingCost(option));
    setShippingPreference(option);
  };

  useEffect(() => {
    if (isFedexShippingEnabled) {
      return;
    }

    if (!shippingPreference) {
      if (shippingPreferenceCost !== false && shippingPreferenceCost !== 0) {
        setShippingPreferenceCost(0);
      }
      return;
    }

    const selectedOptionKey = getShippingPreferenceOptionKey(shippingPreference);
    const matchedOption = shippingPreferenceOptions.find(
      (option) => getShippingPreferenceOptionKey(option) === selectedOptionKey,
    );

    if (!matchedOption) {
      setShippingPreference(false);
      setShippingPreferenceCost(0);
      return;
    }

    const nextShippingCost = getShippingCost(matchedOption);
    if (!isSameShippingPreferenceOption(shippingPreference, matchedOption)) {
      setShippingPreference(matchedOption);
    }
    if (shippingPreferenceCost !== nextShippingCost) {
      setShippingPreferenceCost(nextShippingCost);
    }
  }, [
    getShippingCost,
    isFedexShippingEnabled,
    shippingPreference,
    shippingPreferenceCost,
    shippingPreferenceOptions,
  ]);

  const handleApplicationDate = (date) => {
    console.log(date);
    setApplicationDate(date);
  };

  const handleAddProductCode = (value) => {
    const nextSelectedOptions = Array.isArray(value) ? value : [];

    if (nextSelectedOptions.length === 0) {
      setProductCodes([]);
      setProductCodeData([]);
      setSelectedManufacturerID(caseScopedManufacturerID);
      setSelectedProductGroupID(caseScopedProductGroupID);
      return;
    }

    const targetManufacturerID = toComparableId(
      nextSelectedOptions[0]?.ManufacturerID,
    );
    const targetProductGroupID = toComparableId(
      nextSelectedOptions[0]?.ProductGroupID,
    );
    const normalizedSelectedOptions =
      targetManufacturerID !== null && targetProductGroupID !== null
      ? nextSelectedOptions.filter(
          (option) =>
            matchesSelectedProductScope(
              option,
              targetManufacturerID,
              targetProductGroupID,
            ),
        )
      : nextSelectedOptions;

    setSelectedManufacturerID(targetManufacturerID);
    setSelectedProductGroupID(targetProductGroupID);
    setProductCodes(normalizedSelectedOptions);

    setProductCodeData((prev) =>
      normalizedSelectedOptions.map((option) => {
        const existing = prev.find(
          (item) => Number(item?.ProductID) === Number(option?.ProductID),
        );

        return {
          ID: option?.ProductID,
          ProductID: option?.ProductID,
          ProductCode: option?.ProductCode,
          ProductName: option?.ProductName,
          ManufacturerID: option?.ManufacturerID,
          ManufacturerName: option?.ManufacturerName,
          ProductGroupID: option?.ProductGroupID,
          ProductGroupName: option?.ProductGroupName,
          CollectPayment: option?.CollectPayment !== false,
          CustomShipping: normalizeBoolean(option?.CustomShipping),
          UseFedexShipping: normalizeBoolean(option?.UseFedexShipping),
          ShippingCategoryID: getFirstPositiveNumber(option?.ShippingCategoryID),
          RequireProcedureInfo: option?.RequireProcedureInfo,
          ManageStock: isStockManaged(option),
          StockQty: getStockQty(option),
          Price: Number(option?.Price ?? 0),
          Quantity: existing
            ? Math.min(Number(existing.Quantity ?? 0), isStockManaged(option) ? getStockQty(option) : Number(existing.Quantity ?? 0))
            : isStockManaged(option) && getStockQty(option) === 0
              ? 0
              : 1,
        };
      }),
    );
  };

  useEffect(() => {
    if (shouldShowProcedureStep) {
      return;
    }

    setProviderName(undefined);
    setApplicationDate(undefined);
    setFirstNameInitials("");
    setLastNameInitials("");
    setApplicationNumber(undefined);
  }, [shouldShowProcedureStep]);

  const handleIncrementQuantityChange = (product) => {
    setProductCodeData((prev) =>
      prev.map((item) =>
        Number(item?.ProductID) === Number(product?.ProductID)
          ? {
              ...item,
              Quantity: isStockManaged(item)
                ? Math.min(getStockQty(item), Number(item.Quantity ?? 0) + 1)
                : Number(item.Quantity ?? 0) + 1,
            }
          : item,
      ),
    );
  };

  const handleDecrementQuantityChange = (product) => {
    setProductCodeData((prev) =>
      prev.map((item) =>
        Number(item?.ProductID) === Number(product?.ProductID)
          ? { ...item, Quantity: Math.max(item?.Quantity - 1, 0) }
          : item,
      ),
    );
  };

  const handleSetQuantityChange = (product, value) => {
    const parsed = Math.max(0, Math.floor(Number(value) || 0));
    setProductCodeData((prev) =>
      prev.map((item) =>
        Number(item?.ProductID) === Number(product?.ProductID)
          ? {
              ...item,
              Quantity: isStockManaged(item)
                ? Math.min(getStockQty(item), parsed)
                : parsed,
            }
          : item,
      ),
    );
  };

  const orderProducts = useMemo(
    () =>
      (productCodeData || []).filter((item) => Number(item?.Quantity) > 0),
    [productCodeData],
  );
  const requiresPayment = useMemo(() => {
    if (!orderProducts.length) {
      return true;
    }

    return orderProducts.every((item) => item?.CollectPayment !== false);
  }, [orderProducts]);
  const fedexQuoteRequestKey = useMemo(() => {
    return buildFedexQuoteRequestKey({
      effectiveShipToAccountID,
      resolvedShipToAccountDetails,
      orderProducts,
      fedexServiceType: getFedexServiceType(shippingPreference),
    });
  }, [
    effectiveShipToAccountID,
    orderProducts,
    resolvedShipToAccountDetails?.AddressLine1,
    resolvedShipToAccountDetails?.AddressLine2,
    resolvedShipToAccountDetails?.City,
    resolvedShipToAccountDetails?.State,
    resolvedShipToAccountDetails?.Zip,
    shippingPreference,
  ]);
  const isLoadingFedexQuote = fedexQuoteStatus === "loading";
  const hasSuccessfulFedexQuote = useMemo(
    () =>
      isFedexShippingEnabled &&
      fedexQuote &&
      normalizeShippingRateValue(fedexQuote?.shippingCost) !== null &&
      !fedexQuoteError &&
      fedexQuoteStatus === "success" &&
      lastSuccessfulFedexQuoteKey === fedexQuoteRequestKey,
    [
      fedexQuote,
      fedexQuoteError,
      fedexQuoteRequestKey,
      fedexQuoteStatus,
      isFedexShippingEnabled,
      lastSuccessfulFedexQuoteKey,
    ],
  );

  const orderTotal = orderProducts.reduce(
    (sum, item) => sum + item?.Quantity,
    0,
  );

  const priceTotal = orderProducts.reduce(
    (sum, item) => sum + item?.Price * item?.Quantity,
    0,
  );
  const applyFedexQuoteToShippingState = useCallback((quote, selectedOption) => {
    const fedexOption = buildFedexShippingPreferenceOption(quote, selectedOption);
    if (!fedexOption) {
      setShippingPreferenceCost(0);
      return;
    }

    setShippingPreference(fedexOption);
    setShippingPreferenceCost(fedexOption.cost);
  }, []);

  const clearFedexQuoteState = useCallback(({ clearShippingPreference = false } = {}) => {
    fedexQuoteRequestIdRef.current += 1;
    fedexQuoteInFlightKeyRef.current = "";
    setFedexQuote(null);
    setFedexQuoteError("");
    setLastSuccessfulFedexQuoteKey("");
    setPendingFedexQuoteAfterAccountDetails(false);
    setShippingPreferenceCost(0);
    if (clearShippingPreference) {
      setShippingPreference(false);
    }
  }, []);

  useEffect(() => {
    if (
      isFedexShippingEnabled &&
      shippingPreference &&
      !getFedexServiceType(shippingPreference)
    ) {
      clearFedexQuoteState({ clearShippingPreference: true });
      setFedexQuoteStatus("stale");
    }
  }, [
    clearFedexQuoteState,
    isFedexShippingEnabled,
    shippingPreference,
  ]);

  useEffect(() => {
    fedexQuoteLatestKeyRef.current = fedexQuoteRequestKey;

    if (!isFedexShippingEnabled) {
      if (wasFedexShippingEnabledRef.current) {
        clearFedexQuoteState({ clearShippingPreference: true });
      }
      setFedexQuoteStatus("idle");
      wasFedexShippingEnabledRef.current = false;
      previousFedexQuoteRequestKeyRef.current = fedexQuoteRequestKey;
      return;
    }

    wasFedexShippingEnabledRef.current = true;
    if (fedexQuoteInFlightKeyRef.current === fedexQuoteRequestKey) {
      previousFedexQuoteRequestKeyRef.current = fedexQuoteRequestKey;
      return;
    }

    if (previousFedexQuoteRequestKeyRef.current === fedexQuoteRequestKey) {
      return;
    }

    previousFedexQuoteRequestKeyRef.current = fedexQuoteRequestKey;
    clearFedexQuoteState();
    setFedexQuoteStatus("stale");
  }, [
    clearFedexQuoteState,
    fedexQuoteRequestKey,
    isFedexShippingEnabled,
  ]);

  const startFedexQuote = useCallback(async ({
    force = false,
    shippingPreferenceOverride = null,
  } = {}) => {
    if (!isFedexShippingEnabled) {
      return;
    }

    const selectedFedexPreference =
      shippingPreferenceOverride || shippingPreference;
    const fedexServiceType = getFedexServiceType(selectedFedexPreference);

    if (!fedexServiceType) {
      clearFedexQuoteState();
      setFedexQuoteStatus("stale");
      return;
    }

    if (!effectiveShipToAccountID || !orderProducts.length) {
      clearFedexQuoteState();
      setFedexQuoteStatus("stale");
      return;
    }

    if (isLoadingAccountDetails) {
      setPendingFedexQuoteAfterAccountDetails(true);
      setFedexQuoteStatus("stale");
      return;
    }

    const requestKey = shippingPreferenceOverride
      ? buildFedexQuoteRequestKey({
          effectiveShipToAccountID,
          resolvedShipToAccountDetails,
          orderProducts,
          fedexServiceType,
        })
      : fedexQuoteRequestKey;
    if (!requestKey) {
      clearFedexQuoteState();
      setFedexQuoteStatus("stale");
      return;
    }

    if (!force && fedexQuoteInFlightKeyRef.current === requestKey) {
      return;
    }

    if (
      !force &&
      lastSuccessfulFedexQuoteKey === requestKey &&
      fedexQuote &&
      !fedexQuoteError
    ) {
      applyFedexQuoteToShippingState(fedexQuote, selectedFedexPreference);
      setFedexQuoteStatus("success");
      return;
    }

    const currentRequestId = fedexQuoteRequestIdRef.current + 1;
    fedexQuoteRequestIdRef.current = currentRequestId;
    fedexQuoteInFlightKeyRef.current = requestKey;
    fedexQuoteLatestKeyRef.current = requestKey;
    setPendingFedexQuoteAfterAccountDetails(false);
    setFedexQuote(null);
    setFedexQuoteError("");
    setLastSuccessfulFedexQuoteKey("");
    setShippingPreferenceCost(0);
    setFedexQuoteStatus("loading");

    try {
      const apiService = new APIService();
      const response = await apiService.addDataToBackend(
        buildFedexQuotePayload({
          effectiveShipToAccountID,
          resolvedShipToAccountDetails,
          orderProducts,
          fedexServiceType,
        }),
        "newdirectorder/fedex-shipping-rate",
      );
      const quote = response?.data ?? {};

      if (
        fedexQuoteRequestIdRef.current !== currentRequestId ||
        fedexQuoteLatestKeyRef.current !== requestKey
      ) {
        return;
      }

      setFedexQuote(quote);
      setFedexQuoteError("");
      setLastSuccessfulFedexQuoteKey(requestKey);
      setFedexQuoteStatus("success");
      applyFedexQuoteToShippingState(quote, selectedFedexPreference);
    } catch (error) {
      console.error("[NewDirectOrder] Failed to fetch FedEx quote", error);
      if (
        fedexQuoteRequestIdRef.current !== currentRequestId ||
        fedexQuoteLatestKeyRef.current !== requestKey
      ) {
        return;
      }

      setFedexQuote(null);
      setFedexQuoteError(getFedexQuoteErrorMessage(error));
      setLastSuccessfulFedexQuoteKey("");
      setShippingPreferenceCost(0);
      setFedexQuoteStatus("error");
    } finally {
      if (
        fedexQuoteRequestIdRef.current === currentRequestId &&
        fedexQuoteInFlightKeyRef.current === requestKey
      ) {
        fedexQuoteInFlightKeyRef.current = "";
      }
    }
  }, [
    applyFedexQuoteToShippingState,
    clearFedexQuoteState,
    effectiveShipToAccountID,
    fedexQuote,
    fedexQuoteError,
    fedexQuoteRequestKey,
    isFedexShippingEnabled,
    isLoadingAccountDetails,
    lastSuccessfulFedexQuoteKey,
    orderProducts,
    resolvedShipToAccountDetails,
    shippingPreference,
  ]);

  useEffect(() => {
    if (
      !isFedexShippingEnabled ||
      isLoadingAccountDetails ||
      fedexQuoteStatus !== "stale" ||
      !getFedexServiceType(shippingPreference)
    ) {
      return;
    }

    startFedexQuote();
  }, [
    fedexQuoteStatus,
    isFedexShippingEnabled,
    isLoadingAccountDetails,
    shippingPreference,
    startFedexQuote,
  ]);

  useEffect(() => {
    if (!pendingFedexQuoteAfterAccountDetails || isLoadingAccountDetails) {
      return;
    }

    startFedexQuote();
  }, [
    isLoadingAccountDetails,
    pendingFedexQuoteAfterAccountDetails,
    startFedexQuote,
  ]);

  const retryFedexQuote = useCallback(() => {
    startFedexQuote({ force: true });
  }, [startFedexQuote]);

  const processingFeeSummary = useMemo(
    () =>
      calculateProcessingFee({
        paymentTabKey: activeTab?.key,
        priceTotal,
        shippingPreferenceCost,
        requiresPayment,
      }),
    [activeTab?.key, priceTotal, requiresPayment, shippingPreferenceCost],
  );

  const shippingStepNextDisabled = useMemo(() => {
    if (isFedexShippingEnabled) {
      return (
        !getFedexServiceType(shippingPreference) ||
        isLoadingFedexQuote ||
        Boolean(fedexQuoteError) ||
        !hasSuccessfulFedexQuote
      );
    }

    return !shippingPreference;
  }, [
    fedexQuoteError,
    hasSuccessfulFedexQuote,
    isFedexShippingEnabled,
    isLoadingFedexQuote,
    shippingPreference,
  ]);

  const stepConfig = useMemo(() => {
    const steps = [
      {
        key: "products",
        title: "Products",
        Content: ProductsContent,
        Review: ProductsReview,
        nextBtnDisabled: !hasSelectedProducts,
      },
    ];

    if (shouldShowProcedureStep) {
      steps.push({
        key: "procedures",
        title: "Procedures",
        Content: ProcedureContent,
        Review: ProcedureReview,
      });
    }

    steps.push(
      {
        key: "shipping",
        title: "Shipping",
        Content: ShippingContent,
        Review: ShippingReview,
        nextBtnDisabled: shippingStepNextDisabled,
      },
      {
        key: "confirmation",
        title: "Confirmation",
        Content: ConfirmationContent,
        Review: ConfirmationReview,
      },
    );

    return steps;
  }, [
    hasSelectedProducts,
    shippingStepNextDisabled,
    shouldShowProcedureStep,
  ]);

  const maxReachableStepIndex = useMemo(() => {
    let reachableIndex = 0;

    for (let index = 0; index < stepConfig.length - 1; index += 1) {
      if (stepConfig[index]?.nextBtnDisabled) {
        break;
      }

      reachableIndex = index + 1;
    }

    return reachableIndex;
  }, [stepConfig]);

  useEffect(() => {
    setCurrentStep((prev) => Math.min(prev, stepConfig.length - 1));
  }, [stepConfig.length]);

  useEffect(() => {
    setCurrentStep((prev) => Math.min(prev, maxReachableStepIndex));
  }, [maxReachableStepIndex]);

  const fullName = `${firstNameInitials}${lastNameInitials}`;

  const handleStepChange = (value) => {
    if (value === currentStep) {
      return;
    }

    if (value <= maxReachableStepIndex) {
      const currentStepKey = stepConfig[currentStep]?.key;

      if (value > currentStep && currentStepKey === "products" && isFedexShippingEnabled) {
        startFedexQuote();
      }

      setCurrentStep(value);
      return;
    }

    message.error("Complete the required fields before moving ahead.");
  };

  const handleNextStep = useCallback(() => {
    if (stepConfig[currentStep]?.nextBtnDisabled) {
      message.error("Complete the required fields before moving ahead.");
      return;
    }

    const currentStepKey = stepConfig[currentStep]?.key;

    if (currentStepKey === "products" && isFedexShippingEnabled) {
      startFedexQuote();
    }

    setCurrentStep((prev) =>
      Math.min(prev + 1, stepConfig.length - 1),
    );
  }, [
    currentStep,
    isFedexShippingEnabled,
    startFedexQuote,
    stepConfig,
  ]);

  const buildCreateDirectOrderPayload = () => {
    const manufacturerID = getFirstPositiveNumber(
      orderProducts[0]?.ManufacturerID,
      selectedManufacturerID,
    );
    const manufacturerName = getFirstNonEmptyString(
      orderProducts[0]?.ManufacturerName,
      reviewManufacturerName,
    );
    const productGroupID = getFirstPositiveNumber(
      orderProducts[0]?.ProductGroupID,
      selectedProductGroupID,
    );
    const productGroupName = getFirstNonEmptyString(
      orderProducts[0]?.ProductGroupName,
      reviewProductGroupName,
      selectedProductGroupName,
    );
    const shippingCost = Number(shippingPreferenceCost ?? 0);
    const shipToAccount = resolvedShipToAccountDetails ?? {};
    const billToAccount = billToAccountDetails ?? {};
    const providerAccountUserID = getFirstPositiveNumber(providerName?.UserID);
    const selectedShippingPreference = isFedexShippingEnabled
      ? getFirstNonEmptyString(
          shippingPreference?.label,
          shippingPreference?.value,
          shippingPreference,
        )
      : getFirstNonEmptyString(
          shippingPreference?.value,
          shippingPreference?.label,
          shippingPreference,
        );

    return {
      AccountID: normalizedAccountID,
      AccountName: normalizedAccountName,
      OrderPlacedBy: buildUserFullName(user),
      OrderDate: dayjs().toISOString(),
      ShipToAccountID: getFirstPositiveNumber(
        effectiveShipToAccountID,
        normalizedAccountID,
      ),
      ShipToAccountName: getFirstNonEmptyString(
        normalizedShipToName,
        normalizedAccountName,
      ),
      ShipToContactName: getFirstNonEmptyString(shippingContact),
      ShipToContactID: null,
      ShipAddressLine1: getFirstNonEmptyString(shipToAccount?.AddressLine1),
      ShipAddressLine2: getFirstNonEmptyString(shipToAccount?.AddressLine2),
      ShipCity: getFirstNonEmptyString(shipToAccount?.City),
      ShipState: getFirstNonEmptyString(shipToAccount?.State),
      ShipZip: getFirstNonEmptyString(shipToAccount?.Zip),
      ShipPhone: getFirstNonEmptyString(shipToAccount?.Phone),
      ShipFax: getFirstNonEmptyString(shipToAccount?.Fax),
      BillToAccountID: getFirstPositiveNumber(
        billToAccount?.ID,
        accountDetails?.BillingAccountID,
        normalizedAccountID,
      ),
      BillToAccountName: getFirstNonEmptyString(
        billToAccount?.AccountName,
        normalizedAccountName,
      ),
      AccountsPayableContact: getFirstNonEmptyString(providerName?.label),
      AccountsPayableContactID: providerAccountUserID,
      BillAddressLine1: getFirstNonEmptyString(billToAccount?.AddressLine1),
      BillAddressLine2: getFirstNonEmptyString(billToAccount?.AddressLine2),
      BillCity: getFirstNonEmptyString(billToAccount?.City),
      BillState: getFirstNonEmptyString(billToAccount?.State),
      BillZip: getFirstNonEmptyString(billToAccount?.Zip),
      BillPhone: getFirstNonEmptyString(billToAccount?.Phone),
      BillFax: getFirstNonEmptyString(billToAccount?.Fax),
      ManufacturerID: manufacturerID,
      ManufacturerName: manufacturerName,
      ProductGroupID: productGroupID,
      ProductGroupName: productGroupName,
      DistributorID: getFirstPositiveNumber(
        shipToAccount?.DistributorID,
        accountDetails?.DistributorID,
      ),
      DistributorName: getFirstNonEmptyString(
        shipToAccount?.DistributorName,
        accountDetails?.DistributorName,
      ),
      DistributorUserID: getFirstPositiveNumber(
        shipToAccount?.DistributorUserID,
        accountDetails?.DistributorUserID,
      ),
      DistributorUsername: getFirstNonEmptyString(
        shipToAccount?.DistributorUsername,
        accountDetails?.DistributorUsername,
      ),
      NumberOfProducts: orderTotal,
      OrderTotal: priceTotal,
      ShippingCost: Number.isFinite(shippingCost) ? shippingCost : 0,
      PhysicianName: shouldShowProcedureStep
        ? getFirstNonEmptyString(providerName?.label)
        : "",
      PatientFirstName: shouldShowProcedureStep
        ? getFirstNonEmptyString(firstNameInitials)
        : "",
      PatientLastName: shouldShowProcedureStep
        ? getFirstNonEmptyString(lastNameInitials)
        : "",
      ApplicationNumber: shouldShowProcedureStep
        ? getFirstNonEmptyString(applicationNumber)
        : "",
      PONumber: getFirstNonEmptyString(customPONumber),
      DateOfApplication: shouldShowProcedureStep
        ? toISOStringOrNull(applicationDate)
        : null,
      ShippingPreference: selectedShippingPreference,
      ShippingSelection: {
        Mode: shippingMode,
        OptionCode:
          shippingMode === "standard"
            ? getFirstNonEmptyString(shippingPreference?.value)
            : undefined,
        ShippingRateID:
          shippingMode === "custom"
            ? getFirstPositiveNumber(shippingPreference?.ShippingRateID)
            : undefined,
        FedexServiceType:
          shippingMode === "fedex"
            ? getFedexServiceType(shippingPreference)
            : undefined,
        DisplayedCost: Number.isFinite(shippingCost) ? shippingCost : 0,
      },
      PaymentMethodType:
        activeTab?.key === "bank-transfer" ? "us_bank_account" : "card",
      CaseID: getFirstNonEmptyString(CaseID),
      SubmissionID: submissionIdRef.current,
      OrderProducts: orderProducts.map((item) => ({
        ManufacturerID: getFirstPositiveNumber(
          item?.ManufacturerID,
          manufacturerID,
        ),
        ProductID: getFirstPositiveNumber(item?.ProductID, item?.ID),
        ProductName: getFirstNonEmptyString(item?.ProductName),
        ProductCode: getFirstNonEmptyString(item?.ProductCode),
        ProductGroupID: getFirstPositiveNumber(
          item?.ProductGroupID,
          productGroupID,
        ),
        ProductGroupName: getFirstNonEmptyString(
          item?.ProductGroupName,
          productGroupName,
        ),
        CollectPayment: item?.CollectPayment !== false,
        Quantity: Number(item?.Quantity ?? 0),
        Price: Number(item?.Price ?? 0),
        ProductTotal: Number(
          (Number(item?.Price ?? 0) * Number(item?.Quantity ?? 0)).toFixed(2),
        ),
      })),
    };
  };

  const handleCreateDirectOrder = async () => {
    if (isSubmitting) {
      return;
    }

    if (!normalizedAccountID) {
      message.error("Account information is missing. Please restart the order.");
      return;
    }

    if (!orderProducts.length) {
      message.error("Please add at least one product with quantity.");
      return;
    }

    if (isFedexShippingEnabled) {
      if (isLoadingFedexQuote) {
        message.error("FedEx shipping rate is still loading. Please try again in a moment.");
        return;
      }

      if (fedexQuoteError || !hasSuccessfulFedexQuote) {
        message.error(fedexQuoteError || "Please fetch a FedEx shipping rate before creating the order.");
        return;
      }
    }

    if (!shippingPreference) {
      message.error("Please complete the shipping section before creating the order.");
      return;
    }

    if (isLoadingAccountDetails) {
      message.error("Account details are still loading. Please try again in a moment.");
      return;
    }

    const payload = buildCreateDirectOrderPayload();

    if (!payload.ShipToAccountID || !payload.BillToAccountID) {
      message.error("Shipping or billing account details are unavailable.");
      return;
    }

    setIsSubmitting(true);
    try {
      const apiService = new APIService();
      const response = await apiService.addDataToBackend(
        payload,
        "newdirectorder/create",
      );
      const responseData = response?.data?.data ?? {};
      const paymentData = responseData?.payment;
      const checkoutUrl = paymentData?.checkoutUrl;
      const paymentRequired = paymentData?.required !== false;
      const createdOrderID = getFirstPositiveNumber(
        responseData?.ID,
        response?.data?.ID,
      );
      const createdInvoiceID = getFirstPositiveNumber(
        responseData?.InvoiceID,
      );
      const viewOrderPath =
        responseData?.viewOrderPath ||
        paymentData?.successRedirectPath ||
        (createdOrderID ? `/ViewNewDirectOrder/${createdOrderID}` : null);
      const createdViewOrderID =
        createdOrderID ?? getOrderIdFromViewOrderPath(viewOrderPath);

      if (!viewOrderPath && !checkoutUrl) {
        throw new Error(
          "Direct order was created, but no redirect target was returned.",
        );
      }

      if (checkoutUrl && viewOrderPath) {
        if (createdViewOrderID) {
          setCheckoutLaunchIntent(createdViewOrderID, checkoutUrl);
        }

        message.success("Redirecting to secure checkout...");
        navigate(viewOrderPath, {
          replace: true,
          state: createdInvoiceID ? { InvoiceID: createdInvoiceID } : undefined,
        });
        return;
      }

      if (paymentData?.status === "failed") {
        message.warning(
          paymentData?.error ||
            "Direct order was created, but checkout could not be started.",
        );

        if (viewOrderPath) {
          navigate(viewOrderPath, {
            replace: paymentRequired,
            state: createdInvoiceID ? { InvoiceID: createdInvoiceID } : undefined,
          });
          return;
        }
      }

      message.success(
        paymentRequired
          ? "Direct order created successfully."
          : "Direct order saved successfully.",
      );

      if (viewOrderPath) {
        navigate(viewOrderPath, {
          state: createdInvoiceID ? { InvoiceID: createdInvoiceID } : undefined,
        });
        return;
      }

      throw new Error("Unable to determine the created direct order route.");
    } catch (error) {
      console.error("[NewDirectOrder] Failed to create direct order", error);
      const latestFedexQuote = error?.response?.data?.quote;
      if (error?.response?.status === 409 && latestFedexQuote && isFedexShippingEnabled) {
        applyFedexQuoteToShippingState(latestFedexQuote, shippingPreference);
        setFedexQuote(latestFedexQuote);
        setFedexQuoteError("");
        setLastSuccessfulFedexQuoteKey(fedexQuoteRequestKey);
        setFedexQuoteStatus("success");
      }
      message.error(
        error?.response?.data?.code === "INSUFFICIENT_STOCK"
          ? formatStockShortageMessage(error?.response?.data?.shortages)
          : error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create direct order. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (user && !canAccessPage) {
    return null;
  }

  const { Content, Review, nextBtnDisabled } = stepConfig[currentStep];
  const isLastStep = currentStep === stepConfig.length - 1;

  return (
    <>
      {isSubmitting && (
        <Spin
          fullscreen
          indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
        />
      )}
      <div
        className="content-order-wrapper"
        style={isPhone ? { flexDirection: "column" } : {}}
      >
        <div className="direct-order-content-container">
          <TitleBar
            label={
              <div className="direct-order-model-top">
                <Text variant="h1">New Order</Text>
                <Tags
                  tagBackground="blueBackground"
                  textColor="primaryColor"
                  tagText="Direct"
                />
              </div>
            }
            rightFilterBarComponents={
              <Text variant="h1" color="lightText">
                {normalizedAccountName}
              </Text>
            }
          />
          <div className="direct-order-wrapper-container">
            <ConfigProvider
              theme={{
                token: {
                  fontFamily: "Figtree, sans-serif",
                  colorText: colors["primaryColor"],
                  colorPrimary: colors["primaryColor"],
                },
                components: {
                  Steps: {
                    iconSize: 35,
                  },
                },
              }}
            >
              <Steps
                className="custom-steps"
                current={currentStep}
                onChange={handleStepChange}
                items={stepConfig.map((step, index) => ({
                  title: step.title,
                  disabled: index > maxReachableStepIndex,
                }))}
              />
            </ConfigProvider>

            <Content
              productCodes={productCodes}
              productCodeData={productCodeData}
              productOptions={availableProductOptions}
              isLoadingProductOptions={isLoadingProductOptions}
              selectedManufacturerName={selectedManufacturerName}
              selectedProductGroupName={selectedProductGroupName}
              productGroupName={reviewProductGroupName || selectedProductGroupName}
              placedByName={placedByName}
              handleAddProductCode={handleAddProductCode}
              handleIncrementQuantityChange={handleIncrementQuantityChange}
              handleDecrementQuantityChange={handleDecrementQuantityChange}
              handleSetQuantityChange={handleSetQuantityChange}
              CaseID={CaseID}
              setProviderName={setProviderName}
              providerName={providerName}
              providerOptions={providerOptions}
              isLoadingProviderOptions={isLoadingProviderOptions}
              onProviderCreated={handleProviderCreated}
              handleApplicationDate={handleApplicationDate}
              applicationDate={applicationDate}
              setFirstNameInitials={setFirstNameInitials}
              firstNameInitials={firstNameInitials}
              setLastNameInitials={setLastNameInitials}
              lastNameInitials={lastNameInitials}
              setApplicationNumber={setApplicationNumber}
              applicationNumber={applicationNumber}
              shippingPreference={shippingPreference}
              shippingPreferenceOptions={shippingPreferenceOptions}
              shippingPreferencePlaceholder={shippingPreferencePlaceholder}
              isShippingPreferenceDisabled={isShippingPreferenceDisabled}
              handleShippingPreferenceChange={handleShippingPreferenceChange}
              isFedexShippingEnabled={isFedexShippingEnabled}
              fedexQuote={fedexQuote}
              isLoadingFedexQuote={isLoadingFedexQuote}
              fedexQuoteError={fedexQuoteError}
              onRetryFedexQuote={retryFedexQuote}
              shippingContact={shippingContact}
              setShippingContact={setShippingContact}
              shippingPreferenceCost={shippingPreferenceCost}
              customPONumber={customPONumber}
              setCustomPONumber={setCustomPONumber}
              accountID={normalizedAccountID}
              accountName={normalizedAccountName}
              shipToAccountID={ShipToAccountID}
              shipToAccountName={normalizedShipToName}
              shipToAccountDetails={resolvedShipToAccountDetails}
              billToAccountDetails={billToAccountDetails}
              fullName={fullName}
              procedureTagText={procedureTagText}
              shippingContactTagText=""
              isPhone={isPhone}
              requiresProcedureInfo={shouldShowProcedureStep}
            />
          </div>
        </div>
        <div
          className="direct-order-review-container"
          style={
            isPhone
              ? { maxWidth: "300px" }
              : { width: "500px", maxHeight: "100vh", overflowY: "auto" }
          }
        >
          <Review
            productCodeData={orderProducts}
            orderTotal={orderTotal}
            priceTotal={priceTotal}
            providerName={providerName}
            applicationDate={applicationDate}
            firstNameInitials={firstNameInitials}
            lastNameInitials={lastNameInitials}
            CaseID={CaseID}
            applicationNumber={applicationNumber}
            shippingContact={shippingContact}
            accountID={AccountID}
            accountName={normalizedAccountName}
            shipToAccountID={ShipToAccountID}
            shipToAccountName={normalizedShipToName}
            shipToAccountDetails={resolvedShipToAccountDetails}
            billToAccountDetails={billToAccountDetails}
            manufacturerName={reviewManufacturerName}
            productGroupName={reviewProductGroupName}
            placedByName={placedByName}
            shippingPreferenceCost={shippingPreferenceCost}
            requiresPayment={requiresPayment}
            activeTab={activeTab}
            RENDER_PAYMENT_OPTIONS={RENDER_PAYMENT_OPTIONS}
            cardProcessingFee={processingFeeSummary.cardProcessingFee}
            bankProcessingFee={processingFeeSummary.bankProcessingFee}
            processingFee={processingFeeSummary.processingFee}
            processingFeeLabel={processingFeeSummary.processingFeeLabel}
            shippingPreference={shippingPreference}
            procedureTagText={procedureTagText}
            shippingContactTagText=""
            isSubmitting={isSubmitting}
            isLoadingAccountDetails={isLoadingAccountDetails}
            onCreateDirectOrder={handleCreateDirectOrder}
            isPhone={isPhone}
          />

          {!isLastStep && (
            <div className="direct-order-review-child-content-container">
              <Button
                variant="small-ghost"
                icon={<LeftSmallArrowIcon />}
                onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
              >
                Back
              </Button>
              <Button
                variant="blue-button"
                subVariant="primary"
                icon={<RightSmallArrowIcon />}
                disabled={nextBtnDisabled}
                onClick={handleNextStep}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NewDirectOrder;
